import { NodeType, type StreamType, type WorkflowNode } from '../nodes/NodeTypes';

export interface FilterPreset {
  id: string;
  label: string;
  description: string;
  category: string;
  streamType?: StreamType;
  operation: 'filter' | 'option';
  command: string;
  params: Record<string, string>;
  paramOptions?: Record<string, string[]>;
  paramLabels?: Record<string, string>;
  inputCount?: number;
  beginner: boolean;
}

export const FILTER_PRESETS: FilterPreset[] = [
  {
    id: 'scale', label: '缩放', description: '调整输出画面尺寸。',
    category: '画面', streamType: 'video', operation: 'filter', command: 'scale={width}:{height}',
    params: { width: '1280', height: '720' }, beginner: true,
  },
  {
    id: 'crop', label: '裁剪', description: '裁切画面区域，宽高建议使用偶数。',
    category: '画面', streamType: 'video', operation: 'filter', command: 'crop={width}:{height}:{x}:{y}',
    params: { width: '640', height: '480', x: '0', y: '0' }, beginner: true,
  },
  {
    id: 'transpose', label: '旋转', description: '按 90 度步进旋转画面。',
    category: '画面', streamType: 'video', operation: 'filter', command: 'transpose={direction}',
    params: { direction: 'clock' }, paramOptions: { direction: ['clock', 'cclock', 'clock_flip', 'cclock_flip'] },
    beginner: true,
  },
  {
    id: 'hflip', label: '水平翻转', description: '镜像翻转画面。',
    category: '画面', streamType: 'video', operation: 'filter', command: 'hflip',
    params: {}, beginner: true,
  },
  {
    id: 'trim', label: '截取片段', description: '从素材中截取指定起点和时长。',
    category: '时间', streamType: 'video', operation: 'filter',
    command: 'trim=start={start}:duration={duration},setpts=PTS-STARTPTS',
    params: { start: '0', duration: '10' }, beginner: true,
  },
  {
    id: 'fps', label: '帧率转换', description: '将画面转换为指定帧率。',
    category: '时间', streamType: 'video', operation: 'filter', command: 'fps={fps}',
    params: { fps: '30' }, beginner: false,
  },
  {
    id: 'vflip', label: '垂直翻转', description: '上下翻转画面。',
    category: '画面', streamType: 'video', operation: 'filter', command: 'vflip',
    params: {}, beginner: false,
  },
  {
    id: 'deinterlace', label: '去隔行', description: '使用 YADIF 还原隔行扫描画面。',
    category: '画面', streamType: 'video', operation: 'filter',
    command: 'yadif=mode={mode}:parity={parity}:deint={deint}',
    params: { mode: 'send_frame', parity: 'auto', deint: 'all' },
    paramOptions: {
      mode: ['send_frame', 'send_field', 'send_frame_nospatial', 'send_field_nospatial'],
      parity: ['auto', 'tff', 'bff'], deint: ['all', 'interlaced'],
    },
    paramLabels: { mode: '输出模式', parity: '场序', deint: '处理范围' }, beginner: false,
  },
  {
    id: 'hqdn3d', label: '视频降噪', description: '减少亮度和色度的空间与时间噪点。',
    category: '效果', streamType: 'video', operation: 'filter',
    command: 'hqdn3d={lumaSpatial}:{chromaSpatial}:{lumaTemporal}:{chromaTemporal}',
    params: { lumaSpatial: '4', chromaSpatial: '3', lumaTemporal: '6', chromaTemporal: '4.5' },
    paramLabels: { lumaSpatial: '亮度空间降噪', chromaSpatial: '色度空间降噪', lumaTemporal: '亮度时间降噪', chromaTemporal: '色度时间降噪' }, beginner: false,
  },
  {
    id: 'setsar', label: '像素宽高比', description: '设置显示像素宽高比，不改变帧尺寸。',
    category: '画面', streamType: 'video', operation: 'filter', command: 'setsar={ratio}',
    params: { ratio: '1' }, paramLabels: { ratio: '宽高比' }, beginner: false,
  },
  {
    id: 'speed-video', label: '视频速度', description: '调整视频播放速度，1 为原速。',
    category: '时间', streamType: 'video', operation: 'filter', command: 'setpts=PTS/{speed}',
    params: { speed: '1.5' }, beginner: true,
  },
  {
    id: 'fade-in', label: '淡入', description: '在指定时间段内淡入画面。',
    category: '时间', streamType: 'video', operation: 'filter', command: 'fade=t=in:st={start}:d={duration}',
    params: { start: '0', duration: '1' }, beginner: true,
  },
  {
    id: 'fade-audio', label: '音频淡入', description: '让声音在指定时长内渐入。',
    category: '音频', streamType: 'audio', operation: 'filter', command: 'afade=t=in:st={start}:d={duration}',
    params: { start: '0', duration: '1' }, beginner: true,
  },
  {
    id: 'fade-audio-out', label: '音频淡出', description: '让声音在指定时长内渐出。',
    category: '音频', streamType: 'audio', operation: 'filter', command: 'afade=t=out:st={start}:d={duration}',
    params: { start: '9', duration: '1' }, beginner: false,
  },
  {
    id: 'fade-out', label: '淡出', description: '在指定时间段内淡出画面。',
    category: '时间', streamType: 'video', operation: 'filter', command: 'fade=t=out:st={start}:d={duration}',
    params: { start: '9', duration: '1' }, beginner: false,
  },
  {
    id: 'brightness', label: '亮度与对比度', description: '微调亮度、对比度和饱和度。',
    category: '色彩', streamType: 'video', operation: 'filter',
    command: 'eq=brightness={brightness}:contrast={contrast}:saturation={saturation}',
    params: { brightness: '0', contrast: '1', saturation: '1' }, beginner: true,
  },
  {
    id: 'blur', label: '模糊', description: '使用方框模糊柔化画面。',
    category: '效果', streamType: 'video', operation: 'filter', command: 'boxblur={radius}:{power}',
    params: { radius: '4', power: '1' }, beginner: false,
  },
  {
    id: 'sharpen', label: '锐化', description: '提升亮度细节的边缘锐度。',
    category: '效果', streamType: 'video', operation: 'filter', command: 'unsharp=5:5:{amount}:5:5:0',
    params: { amount: '1' }, beginner: false,
  },
  {
    id: 'volume', label: '音量', description: '调整音频增益，1 为原音量。',
    category: '音频', streamType: 'audio', operation: 'filter', command: 'volume={volume}',
    params: { volume: '1.25' }, beginner: true,
  },
  {
    id: 'speed-audio', label: '音频速度', description: '调整音频速度，范围 0.5–2。',
    category: '音频', streamType: 'audio', operation: 'filter', command: 'atempo={speed}',
    params: { speed: '1.5' }, beginner: false,
  },
  {
    id: 'highpass', label: '高通滤波', description: '衰减指定频率以下的声音。',
    category: '音频', streamType: 'audio', operation: 'filter', command: 'highpass=f={frequency}',
    params: { frequency: '120' }, beginner: false,
  },
  {
    id: 'lowpass', label: '低通滤波', description: '衰减指定频率以上的声音。',
    category: '音频', streamType: 'audio', operation: 'filter', command: 'lowpass=f={frequency}',
    params: { frequency: '12000' }, beginner: false,
  },
  {
    id: 'loudnorm', label: '响度标准化', description: '按 EBU R128 目标响度统一音量。',
    category: '音频', streamType: 'audio', operation: 'filter',
    command: 'loudnorm=I={integrated}:TP={truePeak}:LRA={range}',
    params: { integrated: '-16', truePeak: '-1.5', range: '11' },
    paramLabels: { integrated: '目标响度 I (LUFS)', truePeak: '真峰值 TP (dBTP)', range: '响度范围 LRA' }, beginner: false,
  },
  {
    id: 'aresample', label: '音频重采样', description: '设置音频采样率，例如 44100 或 48000 Hz。',
    category: '音频', streamType: 'audio', operation: 'filter', command: 'aresample={sampleRate}',
    params: { sampleRate: '48000' }, paramLabels: { sampleRate: '采样率 (Hz)' }, beginner: false,
  },
  {
    id: 'silenceremove', label: '移除开头静音', description: '检测并移除音频开头的静音段。',
    category: '音频', streamType: 'audio', operation: 'filter', command: 'silenceremove=start_periods={periods}:start_threshold={threshold}dB',
    params: { periods: '1', threshold: '-50' }, paramLabels: { periods: '静音段数', threshold: '静音阈值 (dB)' }, beginner: false,
  },
  {
    id: 'compressor', label: '动态压缩', description: '收窄音频的动态范围。',
    category: '音频', streamType: 'audio', operation: 'filter',
    command: 'acompressor=threshold={threshold}:ratio={ratio}:attack={attack}:release={release}',
    params: { threshold: '0.125', ratio: '2', attack: '20', release: '250' }, beginner: false,
  },
  {
    id: 'overlay', label: '叠加', description: '将第二路视频叠加到主画面。',
    category: '合成', streamType: 'video', operation: 'filter', command: 'overlay=x={x}:y={y}:eof_action=repeat',
    params: { x: 'main_w-overlay_w-24', y: 'main_h-overlay_h-24' }, inputCount: 2, beginner: false,
  },
  {
    id: 'blend', label: '混合', description: '按混合模式合成两路视频。',
    category: '合成', streamType: 'video', operation: 'filter', command: 'blend=all_mode={mode}:all_opacity={opacity}',
    params: { mode: 'overlay', opacity: '0.5' },
    paramOptions: { mode: ['overlay', 'addition', 'multiply', 'screen', 'difference', 'darken', 'lighten'] },
    inputCount: 2, beginner: false,
  },
  {
    id: 'encode-video', label: '视频编码', description: '设置视频编码器。',
    category: '输出选项', operation: 'option', command: '-c:v {codec}',
    params: { codec: 'libx264' }, paramOptions: { codec: ['libx264', 'libx265', 'copy', 'mpeg4', 'av1'] }, beginner: false,
  },
  {
    id: 'video-bitrate', label: '视频码率', description: '设置目标视频码率。',
    category: '输出选项', operation: 'option', command: '-b:v {bitrate}',
    params: { bitrate: '4M' }, beginner: false,
  },
  {
    id: 'encode-audio', label: '音频编码', description: '设置音频编码器。',
    category: '输出选项', operation: 'option', command: '-c:a {codec}',
    params: { codec: 'aac' }, paramOptions: { codec: ['aac', 'libmp3lame', 'libopus', 'copy'] }, beginner: false,
  },
  {
    id: 'audio-bitrate', label: '音频码率', description: '设置目标音频码率。',
    category: '输出选项', operation: 'option', command: '-b:a {bitrate}',
    params: { bitrate: '128k' }, beginner: false,
  },
  {
    id: 'frame-rate', label: '帧率', description: '设置输出帧率。',
    category: '输出选项', operation: 'option', command: '-r {fps}',
    params: { fps: '30' }, beginner: false,
  },
  {
    id: 'container-format', label: '封装格式', description: '显式指定输出容器格式；通常由扩展名自动推断。',
    category: '输出选项', operation: 'option', command: '-f {format}',
    params: { format: 'matroska' }, paramOptions: { format: ['mp4', 'matroska', 'webm', 'mov', 'avi', 'mp3', 'flac'] }, beginner: false,
  },
  {
    id: 'max-rate', label: '最大码率', description: '限制瞬时峰值码率，通常与缓冲区大小一起设置。',
    category: '输出选项', operation: 'option', command: '-maxrate {maxRate}',
    params: { maxRate: '6M' }, paramLabels: { maxRate: '最大码率' }, beginner: false,
  },
  {
    id: 'buffer-size', label: '码率缓冲区', description: '设置码率控制缓冲区大小。',
    category: '输出选项', operation: 'option', command: '-bufsize {bufferSize}',
    params: { bufferSize: '12M' }, paramLabels: { bufferSize: '缓冲区大小' }, beginner: false,
  },
  {
    id: 'encoder-threads', label: '编码线程', description: '限制编码线程数；0 表示由 FFmpeg 自动选择。',
    category: '输出选项', operation: 'option', command: '-threads {count}',
    params: { count: '0' }, paramOptions: { count: ['0', '2', '4', '8'] }, paramLabels: { count: '线程数' }, beginner: false,
  },
  {
    id: 'overwrite-output', label: '覆盖已有文件', description: '输出文件已存在时直接覆盖。',
    category: '输出选项', operation: 'option', command: '-y', params: {}, beginner: false,
  },
  {
    id: 'no-overwrite-output', label: '禁止覆盖文件', description: '输出文件已存在时停止并提示。',
    category: '输出选项', operation: 'option', command: '-n', params: {}, beginner: false,
  },
  {
    id: 'shortest-output', label: '按最短流结束', description: '任一输出流结束时停止编码。',
    category: '输出选项', operation: 'option', command: '-shortest', params: {}, beginner: false,
  },
];

export const BEGINNER_PRESETS = FILTER_PRESETS.filter((preset) => preset.beginner);

export function getPreset(id: string): FilterPreset | undefined {
  return FILTER_PRESETS.find((preset) => preset.id === id);
}

export function createFilterNode(
  preset: FilterPreset,
  id: string,
  position: { x: number; y: number },
): WorkflowNode {
  return {
    id,
    type: NodeType.PROCESSING,
    position,
    data: {
      label: preset.label,
      category: preset.category,
      presetId: preset.id,
      streamType: preset.streamType,
      isAudio: preset.streamType === 'audio',
      operation: preset.operation,
      command: preset.command,
      params: { ...preset.params },
      paramOptions: preset.paramOptions,
      inputCount: preset.inputCount || 1,
      helpText: preset.description,
      paramLabels: preset.paramLabels,
    },
  };
}
